import type { CustomObjectField } from '@kizen/api/custom-object';
import type {
  ClientCategory,
  GetClientCategoryParams,
  SearchAllFieldsParams,
  SearchFieldsOptions,
} from '@kizen/api/client';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithRequired } from './types';

import {
  getAllClientFields,
  getClientCategories,
  getClientFields,
} from '@kizen/api/client';
import { getResource } from './resource';
import { curry1 } from './utils';

type AllClientFieldsQueryOptions<TData = CustomObjectField[]> = Omit<
  AllClientFieldsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  params?: SearchAllFieldsParams;
};

type ClientFieldsQueryOptions<TData = CustomObjectField[]> = Omit<
  ClientFieldsQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  body?: SearchFieldsOptions['body'];
  params?: SearchFieldsOptions['params'];
};

type ClientCategoriesQueryOptions<TData = ClientCategory[]> = Omit<
  ClientCategoriesQuery<TData>,
  'queryFn' | 'queryKey'
> & {
  params?: GetClientCategoryParams;
};

type AllClientFieldsQuery<TData = CustomObjectField[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    DefaultError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['client', 'fields', 'settings-search']>,
        SearchAllFieldsParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type ClientCategoriesQuery<TData = ClientCategory[]> = WithRequired<
  QueryObserverOptions<
    ClientCategory[],
    DefaultError,
    TData,
    ClientCategory[],
    [Resource<Readonly<['client', 'categories']>, GetClientCategoryParams>]
  >,
  'queryFn' | 'queryKey'
>;

type ClientFieldsQuery<TData = CustomObjectField[]> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    DefaultError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['client', 'fields', 'search']>,
        SearchFieldsOptions['params'],
        SearchFieldsOptions['body']
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const allClientFieldsQuery = <TData = CustomObjectField[]>(
  axios: AxiosInstance,
  options: AllClientFieldsQueryOptions<TData> = {}
): AllClientFieldsQuery<TData> => {
  const path = ['client', 'fields', 'settings-search'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getAllClientFields(axios, { params: qk.params, signal });
    },
    ...queryOptions,
  };
};

export const clientCategoriesQuery = <TData = ClientCategory[]>(
  axios: AxiosInstance,
  options: ClientCategoriesQueryOptions<TData> = {}
): ClientCategoriesQuery<TData> => {
  const path = ['client', 'categories'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getClientCategories(axios, { params: qk.params, signal });
    },
    ...queryOptions,
  };
};

export const clientFieldsQuery = <TData = CustomObjectField[]>(
  axios: AxiosInstance,
  options: ClientFieldsQueryOptions<TData> = {}
): ClientFieldsQuery<TData> => {
  const path = ['client', 'fields', 'search'] as const;
  const { body, params, ...queryOptions } = options;
  const resource = getResource(path, { body, params, method: 'POST' });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getClientFields(axios, {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

class Queries {
  public allClientFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof allClientFieldsQuery<TData>>>
  ) => ReturnType<typeof allClientFieldsQuery<TData>>;
  public clientCategoriesQuery: <TData = ClientCategory[]>(
    ...args: Drop<Parameters<typeof clientCategoriesQuery<TData>>>
  ) => ReturnType<typeof clientCategoriesQuery<TData>>;
  public clientFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof clientFieldsQuery<TData>>>
  ) => ReturnType<typeof clientFieldsQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.allClientFieldsQuery = curry1(allClientFieldsQuery)(axios);
    this.clientCategoriesQuery = curry1(clientCategoriesQuery)(axios);
    this.clientFieldsQuery = curry1(clientFieldsQuery)(axios);
  }
}

export type ClientQueries = Queries;

export const defClientQueries = (axios: AxiosInstance): ClientQueries => {
  return new Queries(axios);
};
